import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import {
  Marker,
  type Map,
  type MapMouseEvent,
  type LngLatLike
} from 'mapbox-gl'
import _isEmpty from 'lodash/isEmpty'

import MapPolygonTooltip from '../../../../components/MapPolygonTooltip/MapPolygonTooltip'
import { FEATURES_LAYER_ID } from '../const'
import { type Emissions } from 'types/data'

export default function useHandlePolygonHover(
  map: Map | undefined,
  emissions: Emissions[]
) {
  const [showPolygonTooltip, setShowPolygonTooltip] = useState(false)
  const [hoveredPolygonId, setHoveredPolygonId] = useState<string | null>(null)

  const handleMouseEnter = (event: MapMouseEvent) => {
    if (map == null) return

    map.getCanvas().style.cursor = 'pointer'

    setShowPolygonTooltip(true)

    const features = map.queryRenderedFeatures(event.point, {
      layers: [FEATURES_LAYER_ID]
    })

    const polygonId = features?.[0]?.id
    if (polygonId != null) {
      setHoveredPolygonId(polygonId.toString())
    }
  }

  const handleMouseLeave = (event: MapMouseEvent) => {
    if (map == null) return

    map.getCanvas().style.cursor = ''

    setShowPolygonTooltip(false)
    setHoveredPolygonId(null)
  }

  useEffect(() => {
    if (map == null) return

    map.on('mouseenter', FEATURES_LAYER_ID, handleMouseEnter)
    map.on('mouseleave', FEATURES_LAYER_ID, handleMouseLeave)

    return () => {
      map.off('mouseenter', FEATURES_LAYER_ID, handleMouseEnter)
      map.off('mouseleave', FEATURES_LAYER_ID, handleMouseLeave)
    }
  }, [map])

  useEffect(() => {
    if (map == null || !showPolygonTooltip) return

    const polygonData = emissions.find(
      (emission) => emission.id === hoveredPolygonId
    )

    if (_isEmpty(polygonData)) return

    const markerContainer = document.createElement('div')
    const root = ReactDOM.createRoot(markerContainer)

    root.render(
      React.createElement(MapPolygonTooltip, { emissions: polygonData })
    )

    const marker = new Marker({ element: markerContainer })

    const coordinates = polygonData.feature.geometry.coordinates
    marker.setLngLat(coordinates as unknown as LngLatLike)
    marker.addTo(map)

    return () => {
      marker.remove()
    }
  }, [map, showPolygonTooltip, hoveredPolygonId])
}
